import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const AssignmentResponses = () => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("responseDate");
  const [sortOrder, setSortOrder] = useState("desc");
  const [timeFilter, setTimeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchAssignmentResponses();
  }, [statusFilter]);

  const fetchAssignmentResponses = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `https://dailybackend-zvvt.onrender.com/api/daily-items/assignment-responses?status=${statusFilter}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setResponses(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching assignment responses:", error);
      toast.error("Failed to fetch assignment responses");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const badgeStyles = {
      approved: {
        background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
        color: "white",
        border: "2px solid #10b981",
        boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)",
      },
      denied: {
        background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
        color: "white",
        border: "2px solid #ef4444",
        boxShadow: "0 4px 15px rgba(239, 68, 68, 0.3)",
      },
      pending: {
        background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
        color: "white",
        border: "2px solid #f59e0b",
        boxShadow: "0 4px 15px rgba(245, 158, 11, 0.3)",
      },
    };

    const icons = {
      approved: "✅",
      denied: "❌",
      pending: "⏳",
    };

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "8px 16px",
          borderRadius: "20px",
          fontSize: "14px",
          fontWeight: "600",
          gap: "6px",
          ...badgeStyles[status],
        }}
      >
        {icons[status]} {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getTimeFilteredResponses = (responses) => {
    if (timeFilter === "all") return responses;

    const now = new Date();
    const filtered = responses.filter((response) => {
      const responseDate = new Date(response.responseDate);
      switch (timeFilter) {
        case "today":
          return responseDate.toDateString() === now.toDateString();
        case "week":
          const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
          return responseDate >= weekAgo;
        case "month":
          const monthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
          return responseDate >= monthAgo;
        default:
          return true;
      }
    });
    return filtered;
  };

  // Filter and sort responses
  const filteredAndSortedResponses = getTimeFilteredResponses(responses)
    .filter((response) => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        response.title?.toLowerCase().includes(searchLower) ||
        response.assignedTo?.firstName?.toLowerCase().includes(searchLower) ||
        response.assignedTo?.lastName?.toLowerCase().includes(searchLower) ||
        response.assignedTo?.username?.toLowerCase().includes(searchLower) ||
        response.assignedTo?.email?.toLowerCase().includes(searchLower) ||
        response.category?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "responseDate":
          aValue = new Date(a.responseDate);
          bValue = new Date(b.responseDate);
          break;
        case "title":
          aValue = a.title?.toLowerCase() || "";
          bValue = b.title?.toLowerCase() || "";
          break;
        case "userName":
          aValue = `${a.assignedTo?.firstName || ""} ${
            a.assignedTo?.lastName || ""
          }`.toLowerCase();
          bValue = `${b.assignedTo?.firstName || ""} ${
            b.assignedTo?.lastName || ""
          }`.toLowerCase();
          break;
        case "status":
          aValue = a.assignmentStatus;
          bValue = b.assignmentStatus;
          break;
        case "category":
          aValue = a.category?.toLowerCase() || "";
          bValue = b.category?.toLowerCase() || "";
          break;
        default:
          aValue = a[sortBy];
          bValue = b[sortBy];
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // Pagination
  const totalPages = Math.ceil(
    filteredAndSortedResponses.length / itemsPerPage
  );
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedResponses = filteredAndSortedResponses.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return "⇅";
    return sortOrder === "asc" ? "↑" : "↓";
  };

  const getAnalytics = () => {
    const total = responses.length;
    const approved = responses.filter(
      (r) => r.assignmentStatus === "approved"
    ).length;
    const denied = responses.filter(
      (r) => r.assignmentStatus === "denied"
    ).length;
    const pending = responses.filter(
      (r) => r.assignmentStatus === "pending"
    ).length;

    return {
      total,
      approved,
      denied,
      pending,
      approvalRate: total > 0 ? Math.round((approved / total) * 100) : 0,
      denialRate: total > 0 ? Math.round((denied / total) * 100) : 0,
    };
  };

  const analytics = getAnalytics();

  if (loading) {
    return (
      <div className="card" style={{ textAlign: "center", padding: "60px" }}>
        <div style={{ fontSize: "48px", marginBottom: "20px" }}>🔄</div>
        <h3 style={{ color: "#666", marginBottom: "10px" }}>
          Loading Assignment Responses...
        </h3>
        <p style={{ color: "#999" }}>Please wait while we fetch your data</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "20px" }}>
      {/* Enhanced Header with Analytics */}
      <div
        className="card"
        style={{
          marginBottom: "30px",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "30px",
          }}
        >
          <div>
            <h1
              style={{
                fontSize: "32px",
                fontWeight: "bold",
                margin: "0 0 10px 0",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              📊 Assignment Responses
            </h1>
            <p style={{ margin: 0, opacity: 0.9, fontSize: "16px" }}>
              Monitor and analyze how users respond to your daily item
              assignments
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            {lastUpdated && (
              <span
                style={{ fontSize: "12px", opacity: 0.8, fontStyle: "italic" }}
              >
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={fetchAssignmentResponses}
              className="btn"
              style={{
                background: "rgba(255,255,255,0.2)",
                color: "white",
                border: "1px solid rgba(255,255,255,0.3)",
                fontSize: "14px",
                padding: "10px 20px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Response Analytics Summary */}
        {responses.length > 0 && (
          <>
            <h3
              style={{
                fontSize: "24px",
                fontWeight: "bold",
                marginBottom: "25px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                margin: "0 0 25px 0",
              }}
            >
              📈 Response Analytics Summary
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: "20px",
              }}
            >
              <div
                style={{
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "12px",
                  padding: "20px",
                  textAlign: "center",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <div
                  style={{
                    fontSize: "28px",
                    fontWeight: "bold",
                    marginBottom: "5px",
                  }}
                >
                  {analytics.total}
                </div>
                <div style={{ fontSize: "14px", opacity: 0.9 }}>Total</div>
              </div>
              <div
                style={{
                  background: "rgba(34, 197, 94, 0.9)",
                  borderRadius: "12px",
                  padding: "20px",
                  textAlign: "center",
                  boxShadow: "0 8px 25px rgba(34, 197, 94, 0.3)",
                }}
              >
                <div
                  style={{
                    fontSize: "28px",
                    fontWeight: "bold",
                    marginBottom: "5px",
                  }}
                >
                  {analytics.approved}
                </div>
                <div style={{ fontSize: "14px", opacity: 0.9 }}>Approved</div>
              </div>
              <div
                style={{
                  background: "rgba(239, 68, 68, 0.9)",
                  borderRadius: "12px",
                  padding: "20px",
                  textAlign: "center",
                  boxShadow: "0 8px 25px rgba(239, 68, 68, 0.3)",
                }}
              >
                <div
                  style={{
                    fontSize: "28px",
                    fontWeight: "bold",
                    marginBottom: "5px",
                  }}
                >
                  {analytics.denied}
                </div>
                <div style={{ fontSize: "14px", opacity: 0.9 }}>Denied</div>
              </div>
              <div
                style={{
                  background: "rgba(245, 158, 11, 0.9)",
                  borderRadius: "12px",
                  padding: "20px",
                  textAlign: "center",
                  boxShadow: "0 8px 25px rgba(245, 158, 11, 0.3)",
                }}
              >
                <div
                  style={{
                    fontSize: "28px",
                    fontWeight: "bold",
                    marginBottom: "5px",
                  }}
                >
                  {analytics.pending}
                </div>
                <div style={{ fontSize: "14px", opacity: 0.9 }}>Pending</div>
              </div>
              <div
                style={{
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "12px",
                  padding: "20px",
                  textAlign: "center",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <div
                  style={{
                    fontSize: "28px",
                    fontWeight: "bold",
                    marginBottom: "5px",
                  }}
                >
                  {analytics.approvalRate}%
                </div>
                <div style={{ fontSize: "14px", opacity: 0.9 }}>
                  Approval Rate
                </div>
              </div>
              <div
                style={{
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "12px",
                  padding: "20px",
                  textAlign: "center",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <div
                  style={{
                    fontSize: "28px",
                    fontWeight: "bold",
                    marginBottom: "5px",
                  }}
                >
                  {analytics.denialRate}%
                </div>
                <div style={{ fontSize: "14px", opacity: 0.9 }}>
                  Denial Rate
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Enhanced Filter Controls */}
      <div className="card" style={{ marginBottom: "30px" }}>
        <h3
          style={{
            marginBottom: "25px",
            color: "#333",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          🔍 Filter & Search Controls
        </h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "25px",
          }}
        >
          {/* Status Filter */}
          <div>
            <label
              style={{
                display: "block",
                fontWeight: "600",
                marginBottom: "10px",
                color: "#555",
              }}
            >
              Filter by Status
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {["all", "approved", "denied", "pending"].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className="btn"
                  style={{
                    padding: "8px 16px",
                    fontSize: "14px",
                    background: statusFilter === status ? "#667eea" : "#f1f3f4",
                    color: statusFilter === status ? "white" : "#333",
                    border: statusFilter === status ? "none" : "1px solid #ddd",
                    transform:
                      statusFilter === status ? "scale(1.05)" : "scale(1)",
                  }}
                >
                  {status === "all"
                    ? "🔍 All"
                    : status === "approved"
                    ? "✅ Approved"
                    : status === "denied"
                    ? "❌ Denied"
                    : "⏳ Pending"}
                </button>
              ))}
            </div>
          </div>

          {/* Time Filter */}
          <div>
            <label
              style={{
                display: "block",
                fontWeight: "600",
                marginBottom: "10px",
                color: "#555",
              }}
            >
              Time Period
            </label>
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                border: "2px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                background: "white",
              }}
            >
              <option value="all">📅 All Time</option>
              <option value="today">📆 Today</option>
              <option value="week">📊 This Week</option>
              <option value="month">📈 This Month</option>
            </select>
          </div>

          {/* Search */}
          <div>
            <label
              style={{
                display: "block",
                fontWeight: "600",
                marginBottom: "10px",
                color: "#555",
              }}
            >
              Search
            </label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                placeholder="Search by title, user, category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 40px 12px 40px",
                  border: "2px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#999",
                }}
              >
                🔍
              </span>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#999",
                    fontSize: "16px",
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Sort Controls */}
          <div>
            <label
              style={{
                display: "block",
                fontWeight: "600",
                marginBottom: "10px",
                color: "#555",
              }}
            >
              Sort by
            </label>
            <div style={{ display: "flex", gap: "8px" }}>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  flex: 1,
                  padding: "12px",
                  border: "2px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: "white",
                }}
              >
                <option value="responseDate">📅 Response Date</option>
                <option value="title">📝 Item Title</option>
                <option value="userName">👤 User Name</option>
                <option value="status">🏷️ Status</option>
                <option value="category">📂 Category</option>
              </select>
              <button
                onClick={() =>
                  setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                }
                className="btn"
                style={{
                  padding: "12px 16px",
                  background: "#f1f3f4",
                  border: "2px solid #ddd",
                  fontSize: "16px",
                }}
              >
                {sortOrder === "asc" ? "↑" : "↓"}
              </button>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div
          style={{
            marginTop: "25px",
            padding: "15px",
            background: "#f8f9fa",
            borderRadius: "8px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <span style={{ color: "#666", fontSize: "14px" }}>
            Showing {paginatedResponses.length} of{" "}
            {filteredAndSortedResponses.length} responses
            {searchTerm && ` for "${searchTerm}"`}
            {timeFilter !== "all" && ` (${timeFilter})`}
          </span>
          {(searchTerm || timeFilter !== "all") && (
            <button
              onClick={() => {
                setSearchTerm("");
                setTimeFilter("all");
                setCurrentPage(1);
              }}
              className="btn btn-secondary"
              style={{ fontSize: "12px", padding: "8px 16px" }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Responses List */}
      {filteredAndSortedResponses.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "60px" }}>
          <div style={{ fontSize: "64px", marginBottom: "20px" }}>📭</div>
          <h3 style={{ color: "#666", marginBottom: "10px", fontSize: "24px" }}>
            No assignment responses found
          </h3>
          <p style={{ color: "#999", marginBottom: "30px" }}>
            {statusFilter === "all"
              ? "No users have responded to your assignments yet."
              : `No ${statusFilter} responses found.`}
          </p>
          {(searchTerm || timeFilter !== "all") && (
            <button
              onClick={() => {
                setSearchTerm("");
                setTimeFilter("all");
                setStatusFilter("all");
                setCurrentPage(1);
              }}
              className="btn btn-primary"
            >
              Clear all filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "25px" }}
          >
            {paginatedResponses.map((item) => (
              <div
                key={item._id}
                className="card"
                style={{
                  transition: "all 0.3s ease",
                  border: "2px solid #e1e5e9",
                  position: "relative",
                  overflow: "hidden",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow = "0 15px 35px rgba(0, 0, 0, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 10px 30px rgba(0, 0, 0, 0.1)";
                }}
              >
                {/* Status Indicator Bar */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "4px",
                    background:
                      item.assignmentStatus === "approved"
                        ? "#22c55e"
                        : item.assignmentStatus === "denied"
                        ? "#ef4444"
                        : "#f59e0b",
                  }}
                />

                <div style={{ paddingTop: "10px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "25px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "15px",
                        }}
                      >
                        <h3
                          style={{
                            fontSize: "22px",
                            fontWeight: "bold",
                            color: "#333",
                            margin: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          📦 {item.title}
                        </h3>
                        {getStatusBadge(item.assignmentStatus)}
                      </div>
                      <p
                        style={{
                          color: "#666",
                          marginBottom: "20px",
                          fontSize: "16px",
                          lineHeight: "1.6",
                        }}
                      >
                        {item.description}
                      </p>

                      {/* Information Grid */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(250px, 1fr))",
                          gap: "15px",
                          marginBottom: "20px",
                        }}
                      >
                        <div
                          style={{
                            background:
                              "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                            color: "white",
                            padding: "15px",
                            borderRadius: "12px",
                            boxShadow: "0 4px 15px rgba(59, 130, 246, 0.3)",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "600",
                              marginBottom: "8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            👤 Assigned User
                          </div>
                          <div style={{ fontSize: "16px", fontWeight: "500" }}>
                            {item.assignedTo?.firstName}{" "}
                            {item.assignedTo?.lastName}
                          </div>
                          <div style={{ fontSize: "14px", opacity: 0.9 }}>
                            @{item.assignedTo?.username}
                          </div>
                        </div>

                        <div
                          style={{
                            background:
                              "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                            color: "white",
                            padding: "15px",
                            borderRadius: "12px",
                            boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "600",
                              marginBottom: "8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            📧 Contact Info
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              wordBreak: "break-all",
                              marginBottom: "4px",
                            }}
                          >
                            {item.assignedTo?.email}
                          </div>
                          <div style={{ fontSize: "12px", opacity: 0.9 }}>
                            ID: {item.assignedTo?.uniqueUserId}
                          </div>
                        </div>

                        <div
                          style={{
                            background:
                              "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                            color: "white",
                            padding: "15px",
                            borderRadius: "12px",
                            boxShadow: "0 4px 15px rgba(139, 92, 246, 0.3)",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "600",
                              marginBottom: "8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            📂 Item Details
                          </div>
                          <div
                            style={{ fontSize: "14px", marginBottom: "4px" }}
                          >
                            Category: {item.category}
                          </div>
                          <div style={{ fontSize: "14px" }}>
                            Qty: {item.quantity} | ${item.price || 0}
                          </div>
                        </div>

                        <div
                          style={{
                            background:
                              "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                            color: "white",
                            padding: "15px",
                            borderRadius: "12px",
                            boxShadow: "0 4px 15px rgba(245, 158, 11, 0.3)",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "600",
                              marginBottom: "8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            📅 Assignment Date
                          </div>
                          <div style={{ fontSize: "14px" }}>
                            {formatDate(item.assignmentRequestDate)}
                          </div>
                        </div>

                        <div
                          style={{
                            background:
                              "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
                            color: "white",
                            padding: "15px",
                            borderRadius: "12px",
                            boxShadow: "0 4px 15px rgba(6, 182, 212, 0.3)",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "600",
                              marginBottom: "8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            ⏰ Response Date
                          </div>
                          <div style={{ fontSize: "14px" }}>
                            {formatDate(item.responseDate)}
                          </div>
                        </div>

                        <div
                          style={{
                            background:
                              "linear-gradient(135deg, #64748b 0%, #475569 100%)",
                            color: "white",
                            padding: "15px",
                            borderRadius: "12px",
                            boxShadow: "0 4px 15px rgba(100, 116, 139, 0.3)",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "600",
                              marginBottom: "8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            📊 Response Time
                          </div>
                          <div style={{ fontSize: "14px" }}>
                            {Math.round(
                              (new Date(item.responseDate) -
                                new Date(item.assignmentRequestDate)) /
                                (1000 * 60 * 60)
                            )}{" "}
                            hours
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Response Details */}
                  {item.userResponse && (
                    <div
                      style={{
                        marginTop: "25px",
                        padding: "20px",
                        background:
                          "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                        borderRadius: "12px",
                        border: "2px solid #e2e8f0",
                      }}
                    >
                      <h4
                        style={{
                          fontWeight: "bold",
                          color: "#334155",
                          marginBottom: "15px",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          fontSize: "18px",
                        }}
                      >
                        💬 User Response Details
                      </h4>

                      {item.assignmentStatus === "denied" &&
                        item.userResponse.denialReason && (
                          <div style={{ marginBottom: "20px" }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "10px",
                                color: "#dc2626",
                                fontWeight: "600",
                              }}
                            >
                              ❌ Denial Reason:
                            </div>
                            <div
                              style={{
                                padding: "15px",
                                background: "#fef2f2",
                                border: "2px solid #fecaca",
                                borderRadius: "8px",
                                color: "#dc2626",
                                fontSize: "15px",
                                lineHeight: "1.6",
                              }}
                            >
                              {item.userResponse.denialReason}
                            </div>
                          </div>
                        )}

                      {item.userResponse.responseNote && (
                        <div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "10px",
                              color: "#475569",
                              fontWeight: "600",
                            }}
                          >
                            📝 Additional Note:
                          </div>
                          <div
                            style={{
                              padding: "15px",
                              background: "white",
                              border: "2px solid #e2e8f0",
                              borderRadius: "8px",
                              color: "#475569",
                              fontSize: "15px",
                              lineHeight: "1.6",
                            }}
                          >
                            {item.userResponse.responseNote}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Status Footer */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "25px",
                      paddingTop: "20px",
                      borderTop: "2px solid #f1f5f9",
                    }}
                  >
                    <div>
                      {item.assignmentStatus === "approved" && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            background:
                              "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                            color: "white",
                            padding: "12px 20px",
                            borderRadius: "10px",
                            fontWeight: "600",
                            boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)",
                          }}
                        >
                          <span
                            style={{ fontSize: "18px", marginRight: "8px" }}
                          >
                            ✅
                          </span>
                          Assignment Active & Approved
                        </div>
                      )}
                      {item.assignmentStatus === "denied" && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            background:
                              "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                            color: "white",
                            padding: "12px 20px",
                            borderRadius: "10px",
                            fontWeight: "600",
                            boxShadow: "0 4px 15px rgba(239, 68, 68, 0.3)",
                          }}
                        >
                          <span
                            style={{ fontSize: "18px", marginRight: "8px" }}
                          >
                            ❌
                          </span>
                          Assignment Declined
                        </div>
                      )}
                      {item.assignmentStatus === "pending" && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            background:
                              "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                            color: "white",
                            padding: "12px 20px",
                            borderRadius: "10px",
                            fontWeight: "600",
                            boxShadow: "0 4px 15px rgba(245, 158, 11, 0.3)",
                          }}
                        >
                          <span
                            style={{ fontSize: "18px", marginRight: "8px" }}
                          >
                            ⏳
                          </span>
                          Awaiting User Response
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        color: "#94a3b8",
                        fontSize: "12px",
                        fontFamily: "monospace",
                      }}
                    >
                      Item ID: {item._id.slice(-8)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              style={{
                marginTop: "40px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="btn"
                  style={{
                    padding: "10px 16px",
                    fontSize: "14px",
                    background: currentPage === 1 ? "#f1f3f4" : "white",
                    color: currentPage === 1 ? "#999" : "#333",
                    border: "2px solid #ddd",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  ← Previous
                </button>

                {[...Array(totalPages)].map((_, index) => {
                  const page = index + 1;
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 2 && page <= currentPage + 2)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className="btn"
                        style={{
                          padding: "10px 16px",
                          fontSize: "14px",
                          background:
                            currentPage === page ? "#667eea" : "white",
                          color: currentPage === page ? "white" : "#333",
                          border:
                            currentPage === page
                              ? "2px solid #667eea"
                              : "2px solid #ddd",
                          fontWeight: currentPage === page ? "bold" : "normal",
                        }}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 3 ||
                    page === currentPage + 3
                  ) {
                    return (
                      <span
                        key={page}
                        style={{ padding: "10px 8px", color: "#999" }}
                      >
                        ...
                      </span>
                    );
                  }
                  return null;
                })}

                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="btn"
                  style={{
                    padding: "10px 16px",
                    fontSize: "14px",
                    background:
                      currentPage === totalPages ? "#f1f3f4" : "white",
                    color: currentPage === totalPages ? "#999" : "#333",
                    border: "2px solid #ddd",
                    cursor:
                      currentPage === totalPages ? "not-allowed" : "pointer",
                  }}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AssignmentResponses;
